// spec 260509-system-monitor B-2 / design.md §4.2-4.3：主因 / 瓶颈 / 主模型派生。
//
// 三个纯函数 + 三个 React hook 包装。纯函数不依赖 React，便于单测；② 段 hook
// 维护一个轮播下标（多资源 / 多磁盘按固定节奏轮番展示）。

import { useEffect, useMemo, useState } from 'react';

import { useSystemMetricsStore } from './system-metrics-store';
import { THRESHOLDS, classify, classifyTemp } from './thresholds';
import type {
  ServiceCard,
  SystemMetrics,
  TemperatureSensor,
} from './system-types';

// ---------------------------------------------------------------------------
// 类型
// ---------------------------------------------------------------------------

export type HealthLevel = 'ok' | 'warn' | 'crit';

export interface HealthSummary {
  level: HealthLevel;
  reason: string;
}

export type BottleneckKind = 'cpu' | 'gpu' | 'memory' | 'temperature' | 'disk';

export interface PrimaryBottleneck {
  kind: BottleneckKind;
  /** 当前值（CPU%, GPU%, MEM%, ℃, disk%）。 */
  value: number;
  /** 给 UI 渲染时的友好标签，例如 "GPU · RTX 4090" / "内存" / "存储" / "CPU 温度"。 */
  label: string;
  severity: HealthLevel;
}

export interface PrimaryModelSelection {
  primary: ServiceCard | null;
  secondary: ServiceCard | null;
}

// ---------------------------------------------------------------------------
// deriveHealthSummary（① 段）
// ---------------------------------------------------------------------------

/** spec §4.2 优先级 1：critical 服务持续异常 ≥ 10s。 */
function pickCriticalDownService(services: ServiceCard[]): ServiceCard | null {
  for (const s of services) {
    if (s.tier !== 'critical') continue;
    const dur = s.estimated_unhealthy_for_seconds;
    if (typeof dur === 'number' && dur >= 10) return s;
  }
  return null;
}

function hottestSensor(metrics: SystemMetrics | null): TemperatureSensor | null {
  const sensors = metrics?.temperature?.sensors ?? [];
  if (sensors.length === 0) return null;
  // 风险归一：current / threshold[kind].crit；最高优先。
  let best: { sensor: TemperatureSensor; risk: number } | null = null;
  for (const s of sensors) {
    const t = THRESHOLDS.temp[s.kind] ?? THRESHOLDS.temp.unknown;
    const risk = s.current / t.crit;
    if (best === null || risk > best.risk) best = { sensor: s, risk };
  }
  return best ? best.sensor : null;
}

function hottestMount(metrics: SystemMetrics | null) {
  const mounts = metrics?.disk?.mounts ?? [];
  if (mounts.length === 0) return null;
  return mounts.reduce((a, b) => (b.percent > a.percent ? b : a));
}

/** 整机磁盘占用：所有挂载 used/total 汇总成一个总占用率（"存储展示一个总的"）。 */
function aggregateDiskPercent(metrics: SystemMetrics | null): number | null {
  const mounts = metrics?.disk?.mounts ?? [];
  if (mounts.length === 0) return null;
  const total = mounts.reduce((s, m) => s + m.total_bytes, 0);
  const used = mounts.reduce((s, m) => s + m.used_bytes, 0);
  return total > 0 ? (used / total) * 100 : 0;
}

/** 传感器名（如 "Package id 0"）对用户无意义；按 kind 给中文友好标签。 */
function tempLabel(kind: TemperatureSensor['kind']): string {
  switch (kind) {
    case 'cpu_pkg':
    case 'cpu_core':
      return 'CPU 温度';
    case 'gpu_die':
    case 'gpu_hotspot':
      return 'GPU 温度';
    case 'nvme':
      return '硬盘温度';
    default:
      return '温度';
  }
}

export function deriveHealthSummary(
  metrics: SystemMetrics | null,
  services: ServiceCard[],
): HealthSummary {
  // 优先级 1：服务 critical
  const downService = pickCriticalDownService(services);
  if (downService) {
    return {
      level: 'crit',
      reason: `${downService.display_name ?? downService.name} down`,
    };
  }

  if (metrics === null) {
    return { level: 'ok', reason: '采集中…' };
  }

  // 优先级 2 / 3：资源 critical / warn
  let warnReason: string | null = null;

  const cpuSev = classify(
    metrics.cpu_percent,
    THRESHOLDS.cpu.warn,
    THRESHOLDS.cpu.crit,
  );
  if (cpuSev === 'crit') return { level: 'crit', reason: `CPU ${Math.round(metrics.cpu_percent)}%` };
  if (cpuSev === 'warn' && warnReason === null) warnReason = `CPU ${Math.round(metrics.cpu_percent)}%`;

  for (const g of metrics.gpus) {
    const sev = classify(g.util_percent, THRESHOLDS.gpu_util.warn, THRESHOLDS.gpu_util.crit);
    if (sev === 'crit') return { level: 'crit', reason: `GPU ${Math.round(g.util_percent)}%` };
    if (sev === 'warn' && warnReason === null) warnReason = `GPU ${Math.round(g.util_percent)}%`;
  }

  // 内存：percent / available_bytes / swap_percent 联合
  const memPct = metrics.memory?.percent ?? metrics.mem_percent;
  if (memPct >= THRESHOLDS.mem_percent.crit) return { level: 'crit', reason: `MEM ${Math.round(memPct)}%` };
  if (
    metrics.memory?.available_bytes !== undefined &&
    metrics.memory.available_bytes < THRESHOLDS.available_bytes_crit
  ) {
    return { level: 'crit', reason: `MEM avail ${Math.round(metrics.memory.available_bytes / 1024 / 1024)} MB` };
  }
  const swapPct = metrics.memory?.swap_percent ?? null;
  const memWarn =
    memPct >= THRESHOLDS.mem_percent.warn ||
    (metrics.memory && metrics.memory.available_bytes < THRESHOLDS.available_bytes_warn) ||
    (swapPct !== null && swapPct >= THRESHOLDS.swap_percent_warn);
  if (memWarn && warnReason === null) {
    warnReason = `MEM ${Math.round(memPct)}%`;
  }

  // 温度
  const hot = hottestSensor(metrics);
  if (hot) {
    const sev = classifyTemp(hot.kind, hot.current);
    const label = tempLabel(hot.kind);
    if (sev === 'crit') return { level: 'crit', reason: `${label} ${Math.round(hot.current)}℃` };
    if (sev === 'warn' && warnReason === null) warnReason = `${label} ${Math.round(hot.current)}℃`;
  }

  // 磁盘（任一挂载点 ≥ 95% → crit；≥ 85% → warn）
  const hottestDisk = hottestMount(metrics);
  if (hottestDisk) {
    const sev = classify(
      hottestDisk.percent,
      THRESHOLDS.disk_percent.warn,
      THRESHOLDS.disk_percent.crit,
    );
    if (sev === 'crit') {
      return { level: 'crit', reason: `存储 ${Math.round(hottestDisk.percent)}%` };
    }
    if (sev === 'warn' && warnReason === null) {
      warnReason = `存储 ${Math.round(hottestDisk.percent)}%`;
    }
  }

  if (warnReason !== null) return { level: 'warn', reason: warnReason };
  return { level: 'ok', reason: '全部正常' };
}

// ---------------------------------------------------------------------------
// deriveResourceItems（② 段：可轮播的全部资源候选）
// ---------------------------------------------------------------------------

/**
 * 把当前 metrics 摊平成「可在 ② 段轮番展示」的资源项列表：
 * CPU / 内存 / 每张 GPU / 最热温度 / 每个磁盘挂载（标签统一为「存储」，
 * 多挂载时把挂载点放进 ``detail`` 以便区分）。
 *
 * 排序：crit > warn > ok，同级按当前值降序——所以 ``[0]`` 永远是「此刻最重
 * 的那项」，② 段首帧 / 单测快照都落在它上面;轮播 hook 再从这个顺序往后走。
 */
export function deriveResourceItems(
  metrics: SystemMetrics | null,
): PrimaryBottleneck[] {
  if (metrics === null) return [];

  const items: PrimaryBottleneck[] = [];

  // CPU
  items.push({
    kind: 'cpu',
    value: metrics.cpu_percent,
    label: 'CPU',
    severity: classify(metrics.cpu_percent, THRESHOLDS.cpu.warn, THRESHOLDS.cpu.crit),
  });

  // 内存
  const memPct = metrics.memory?.percent ?? metrics.mem_percent;
  items.push({
    kind: 'memory',
    value: memPct,
    label: '内存',
    severity: classify(memPct, THRESHOLDS.mem_percent.warn, THRESHOLDS.mem_percent.crit),
  });

  // GPU（每张卡一项）
  for (const g of metrics.gpus) {
    items.push({
      kind: 'gpu',
      value: g.util_percent,
      label: `GPU · ${g.name}`,
      severity: classify(g.util_percent, THRESHOLDS.gpu_util.warn, THRESHOLDS.gpu_util.crit),
    });
  }

  // 温度（最热传感器，按 kind 给友好标签，不露 "Package id 0" 这种原始名）
  const hot = hottestSensor(metrics);
  if (hot) {
    items.push({
      kind: 'temperature',
      value: hot.current,
      label: tempLabel(hot.kind),
      severity: classifyTemp(hot.kind, hot.current),
    });
  }

  // 存储（所有挂载汇总成一项总占用率，不分盘）
  const diskPct = aggregateDiskPercent(metrics);
  if (diskPct !== null) {
    items.push({
      kind: 'disk',
      value: diskPct,
      label: '存储',
      severity: classify(diskPct, THRESHOLDS.disk_percent.warn, THRESHOLDS.disk_percent.crit),
    });
  }

  // crit > warn > ok；同 severity 内取 value 最大。
  const severityRank = (s: HealthLevel): number => (s === 'crit' ? 2 : s === 'warn' ? 1 : 0);
  items.sort((a, b) => {
    const r = severityRank(b.severity) - severityRank(a.severity);
    if (r !== 0) return r;
    return b.value - a.value;
  });
  return items;
}

/** 此刻最重的资源项（② 段首帧 / 健康派生用）。轮播从这一项开始往后走。 */
export function derivePrimaryBottleneckRaw(
  metrics: SystemMetrics | null,
): PrimaryBottleneck | null {
  return deriveResourceItems(metrics)[0] ?? null;
}

// ---------------------------------------------------------------------------
// derivePrimaryModel（③ 段）
// ---------------------------------------------------------------------------

export function derivePrimaryModel(services: ServiceCard[]): PrimaryModelSelection {
  const models = services.filter(
    (s) => s.tier === 'critical' && (s.kind === 'llm' || s.kind === 'asr'),
  );
  if (models.length === 0) return { primary: null, secondary: null };
  if (models.length === 1) return { primary: models[0] ?? null, secondary: null };

  const sorted = [...models].sort((a, b) => {
    // 1. critical（lifecycle 异常 + estimated_unhealthy_for_seconds ≥ 10）
    const aCrit = isModelCritical(a);
    const bCrit = isModelCritical(b);
    if (aCrit !== bCrit) return aCrit ? -1 : 1;
    // 2. queued
    const aQ = a.metrics?.waiting ?? 0;
    const bQ = b.metrics?.waiting ?? 0;
    if (aQ !== bQ) return bQ - aQ;
    // 3. active
    const aA = a.metrics?.running ?? 0;
    const bA = b.metrics?.running ?? 0;
    if (aA !== bA) return bA - aA;
    // 4. tok/s
    const aT = a.metrics?.tokens_per_sec ?? 0;
    const bT = b.metrics?.tokens_per_sec ?? 0;
    if (aT !== bT) return bT - aT;
    // 5. LLM 默认在前
    if (a.kind === 'llm' && b.kind !== 'llm') return -1;
    if (b.kind === 'llm' && a.kind !== 'llm') return 1;
    return a.name.localeCompare(b.name);
  });
  return { primary: sorted[0] ?? null, secondary: sorted[1] ?? null };
}

function isModelCritical(s: ServiceCard): boolean {
  const dur = s.estimated_unhealthy_for_seconds;
  return typeof dur === 'number' && dur >= 10;
}

// ---------------------------------------------------------------------------
// React hooks
// ---------------------------------------------------------------------------

export function useDeriveHealthSummary(): HealthSummary {
  const latest = useSystemMetricsStore((s) => s.latest);
  const services = useSystemMetricsStore((s) => s.services);
  return useMemo(() => deriveHealthSummary(latest, services), [latest, services]);
}

/** ② 段轮播节奏：每 4s 切到下一项资源 / 下一块盘。 */
const RESOURCE_ROTATE_MS = 4000;

/**
 * ② 段：把 CPU / 内存 / GPU / 温度 / 各磁盘挂载按固定节奏轮番展示。
 *
 * - 首帧（``step === 0``）落在 ``deriveResourceItems`` 排序后的第 0 项，即
 *   「此刻最重的资源」——告警语义不丢，单测快照也稳定。
 * - 之后每 ``RESOURCE_ROTATE_MS`` 往后挪一项；多块盘也由此自然轮番。
 * - 整机告警另由 ① 段健康徽章常驻，轮播不会把 crit 资源「转走看不到」。
 */
export function useDerivePrimaryBottleneck(): PrimaryBottleneck | null {
  const latest = useSystemMetricsStore((s) => s.latest);
  const items = useMemo(() => deriveResourceItems(latest), [latest]);

  const [step, setStep] = useState(0);
  useEffect(() => {
    // 0 / 1 项无需轮播；项数变化时重置计时器即可。
    if (items.length <= 1) return undefined;
    const id = setInterval(() => setStep((s) => s + 1), RESOURCE_ROTATE_MS);
    return () => clearInterval(id);
  }, [items.length]);

  if (items.length === 0) return null;
  return items[step % items.length] ?? items[0] ?? null;
}

export function useDerivePrimaryModel(): PrimaryModelSelection {
  const services = useSystemMetricsStore((s) => s.services);
  return useMemo(() => derivePrimaryModel(services), [services]);
}

